import React, { Component } from "react";
import PropTypes from "prop-types";
import { Slider } from "antd";
import { FlexibleWidthXYPlot, VerticalRectSeries, CustomSVGSeries, Crosshair, DiscreteColorLegend } from "@unifiedpatents/euclid-react";
import LoadingWrapper from "../../common/loading-wrapper";
import { getFamilyByScore, getPublicationByScore } from "../../../services/api-landscape";
import { range, omit, maxBy } from "lodash";
import { view2cardinalField, viewValues } from "./utils";

const COLOR_P = "#ccc"; // population
const COLOR_S = "#42A5F5"; // subset

class ScoreHistogram extends Component {
  static propTypes = {
    esUrl: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    filter: PropTypes.object,
    filterPopulation: PropTypes.object,
    view: PropTypes.oneOf(viewValues),
    alphaThreshold: PropTypes.number.isRequired,
    alphaBaseline: PropTypes.number.isRequired,
    onSliderChange: PropTypes.func.isRequired
  };

  static defaultProps = {
    filter: {},
    filterPopulation: {},
    view: "pub"
  };

  state = {
    data: [],
    loading: true,
    dataPopulation: [],
    hoveredValues: [],
    sliderInput: this.props.alphaThreshold,
    highlightedGroup: undefined
  };

  componentDidMount() {
    this._isMounted = true;
    this._timeout = 0;
    this._getData(this.props.esUrl, this.props.version, this.props.view, this.props.filter, "data");
    this._getData(this.props.esUrl, this.props.version, this.props.view, this.props.filterPopulation, "dataPopulation");
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this._isMounted) this.setState({ sliderInput: nextProps.alphaThreshold });
    const doNotTrack = ["width", "alphaThreshold"];
    if (JSON.stringify(omit(nextProps, doNotTrack)) !== JSON.stringify(omit(this.props, doNotTrack))) {
      if (this._isMounted) this.setState({ loading: true });
      this._getData(nextProps.esUrl, nextProps.version, nextProps.view, nextProps.filter, "data");
      this._getData(nextProps.esUrl, nextProps.version, nextProps.view, nextProps.filterPopulation, "dataPopulation");
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { view, alphaBaseline } = this.props;
    const { data, dataPopulation, hoveredValues, sliderInput, highlightedGroup } = this.state;
    const scalar = view.startsWith("family") ? 1 : 4; // pat view has more counts than family
    const domainMax =
      dataPopulation.length && sliderInput < dataPopulation.length
        ? maxBy(dataPopulation.slice(sliderInput), "doc_count").doc_count || 1000 * scalar // use max doc_count at selected slider position, use default if empty
        : 1000 * scalar; // default domain max
    const alphaCrosshairValues = [{ x: sliderInput + 1, title: "SIMILARITY", value: sliderInput }];
    return (
      <LoadingWrapper loading={this.state.loading} spinSize="large" style={{ textAlign: "center" }}>
        <Slider
          min={0}
          max={100}
          value={sliderInput || 0}
          onChange={this._onSliderChange}
          railStyle={{ backgroundColor: "#91d5ff" }}
          trackStyle={{ backgroundColor: "#f5f5f5" }} // swap active and inactive color
          style={{ marginLeft: "1%" }}
        />
        <FlexibleWidthXYPlot xDomain={[0, 100]} yDomain={[0, domainMax]} margin={{ left: 0, right: 16, top: 0, bottom: 0 }} height={146} onMouseLeave={this._clearHoveredValues}>
          <VerticalRectSeries
            color={COLOR_P}
            data={dataPopulation.map(d => ({
              k: d.key,
              x: d.key,
              x0: d.key + 0.7,
              y: 0,
              y0: d.doc_count,
              opacity: highlightedGroup === "population" ? 1 : highlightedGroup === "subset" ? 0 : d.key >= sliderInput ? 1 : 0.2
            }))}
            onNearestX={this._updateHoveredValues}
          />
          <VerticalRectSeries
            color={COLOR_S}
            data={data.map(d => ({
              k: d.key,
              x: d.key,
              x0: d.key + 0.7,
              y: 0,
              y0: d.doc_count,
              opacity: highlightedGroup === "subset" ? 1 : highlightedGroup === "population" ? 0 : d.key >= sliderInput ? 1 : 0.2
            }))}
            onNearestX={this._updateHoveredValues}
            animation={true}
          />
          {sliderInput !== alphaBaseline && (
            <Crosshair values={[{ x: alphaBaseline + 1 }]}>
              <span>{`Baseline: ${alphaBaseline}`}</span>
            </Crosshair>
          )}
          <Crosshair values={alphaCrosshairValues} titleFormat={vals => vals[0]} itemsFormat={vals => vals.slice(1)} style={{ box: { backgroundColor: "#bbb" } }} />
          <Crosshair values={hoveredValues} titleFormat={vals => vals[0]} itemsFormat={vals => vals.slice(1)} style={{ box: { marginTop: 50 } }} />
        </FlexibleWidthXYPlot>
        <FlexibleWidthXYPlot xDomain={[0, 100]} yDomain={[0, 20]} margin={{ left: 0, right: 16, top: 0, bottom: 0 }} height={10} onMouseLeave={this._clearHoveredValues}>
          <CustomSVGSeries
            data={data.map(d => ({
              k: d.key + 1,
              x: d.key + 1,
              y: 10,
              size: 7,
              style: {
                opacity:
                  (d.doc_count / (50 * scalar) >= 1 ? 1 : d.doc_count !== 0 ? d.doc_count / (50 * scalar) + 0.3 : 0) *
                  (highlightedGroup === "subset" ? 1 : highlightedGroup === "population" ? 0 : d.key >= sliderInput ? 1 : 0.5),
                fill: COLOR_S
              }
            }))}
            onNearestX={this._updateHoveredValues}
          />
          <Crosshair values={[{ x: sliderInput + 1 }]}>
            <div />
          </Crosshair>
        </FlexibleWidthXYPlot>
        <DiscreteColorLegend
          orientation="horizontal"
          items={[
            { title: "Landscape", key: "population", color: COLOR_P },
            { title: "Filtered Subset", key: "subset", color: COLOR_S }
          ]}
          onItemMouseEnter={item => this.setState({ highlightedGroup: item.key })}
          onItemMouseLeave={() => this.setState({ highlightedGroup: undefined })}
        />
      </LoadingWrapper>
    );
  }

  _getData = (esUrl, version, view, filter, stateKey) => {
    const apiCallPromise = view.startsWith("family")
      ? getFamilyByScore(esUrl, version, omit(filter, `alpha_score`), "alpha_score", 1, view2cardinalField(view))
      : getPublicationByScore(esUrl, version, omit(filter, `alpha_score`), "alpha_score", 1);
    const zeroPad = res => {
      if (res[0] && res[0].key >= 0) {
        const leftPad = range(res[0].key).map(e => ({ key: e, doc_count: 0 }));
        const rightPad = range(Math.min(res[res.length - 1].key + 1, 100), 100).map(e => ({ key: e, doc_count: 0 }));
        return leftPad.concat(res).concat(rightPad);
      } else {
        return [];
      }
    };
    apiCallPromise
      .then(res => {
        if (this._isMounted) this.setState({ [stateKey]: zeroPad(res), loading: false });
      })
      .catch(() => {
        if (this._isMounted) this.setState({ [stateKey]: [], loading: false });
      });
  };

  _updateHoveredValues = val => {
    const { data, dataPopulation } = this.state;
    const alpha = val.k - 1;
    const x = val.x;
    if (this._isMounted)
      this.setState({
        hoveredValues: [
          { x: x, title: "SIMILARITY", value: alpha },
          {
            x: x,
            title: "Landscape",
            value: dataPopulation[alpha] ? dataPopulation[alpha].doc_count : 0
          },
          {
            x: x,
            title: "Filtered Subset",
            value: data[alpha] && dataPopulation[alpha] && data[alpha].doc_count ? `${data[alpha].doc_count} (${((data[alpha].doc_count * 100) / dataPopulation[alpha].doc_count).toFixed(2)}%)` : 0
          }
        ]
      });
  };

  _clearHoveredValues = () => {
    if (this._isMounted) this.setState({ hoveredValues: [] });
  };

  _onSliderChange = val => {
    if (this._isMounted)
      this.setState({
        sliderInput: val
      });
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      this.props.onSliderChange(val);
    }, 500);
  };
}

export default ScoreHistogram;
